import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import UserConversationDialog, { Props as UserConversationDialogProps } from './UserConversationDialog';
import useSendAs, { useEntityInboxUrl } from './sendAs';

type Props = Omit<UserConversationDialogProps, 'sendAs'> & {
  /**
   * A company whose identity must not be used here, because the recipient is one of its own people —
   * writing to your colleague as your own company would drop the message in your own inbox.
   */
  excludeEntityId?: number;
};

/**
 * What "Contact" means for a person, at the 1:1 entry points. Inside a community or company you
 * administer it sends you to that entity's inbox; otherwise it is the ordinary dialog, untouched.
 *
 * A company writes to one person at a time and cannot attach a shared card, so group threads, new
 * conversations and share flows fall straight through.
 */
export default function ConversationWithUser({ excludeEntityId, ...props }: Props) {
  const history = useHistory();
  const location = useLocation();

  const withCwUserId =
    props.conversation.type === 'withUsers' && props.conversation.withCwUserIds.length === 1 && !props.sharedContent
      ? props.conversation.withCwUserIds[0]
      : undefined;

  const sendAs = useSendAs({ excludeId: excludeEntityId });
  const inboxUrl = useEntityInboxUrl(withCwUserId ? { type: 'user', cwUserId: withCwUserId } : undefined);

  // See ConversationWithCompany: navigating from here keeps every contact button unchanged, and
  // landing on the url we are already on just dismisses whatever opened us.
  const goToInbox = sendAs.selected.type === 'company' && !!inboxUrl;
  useEffect(() => {
    if (!goToInbox || !inboxUrl) return;

    if (location.pathname === inboxUrl) {
      props.closeConversation();
    } else {
      history.push(inboxUrl);
    }
  }, [goToInbox, inboxUrl, location.pathname]);

  if (goToInbox) {
    return null;
  }

  return <UserConversationDialog {...props} />;
}
